import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  UserPlus,
  CheckCircle,
  Download,
  Search,
  X,
} from "lucide-react";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import { pdf } from "@react-pdf/renderer";
import PatientPDF from "../../pages/admin/PatientPDF"; // Ajustez le chemin selon votre structure
import {
  VisitReason,
  type Consultation,
  type Doctor,
  type Patient,
  type PatientFormData,
  type PatientWithConstante,
  type MedicalConstant,
} from "../../types";

// Interface pour constante data
interface Constante {
  weight: number;
  height: number;
  bloodPressure: string;
  heartRate: number;
  temperature: number;
  reason: VisitReason;
  recordedAt: string;
  patientId?: number;
  doctorId?: number;
}

interface ConsultationFormBaseProps {
  mode: "create" | "update";
  initialData?: Consultation | null;
  onSubmit?: (data: Consultation | PatientWithConstante) => Promise<void>;
  isSubmitting?: boolean;
  role: "admin" | "doctor";
}

const ConsultationFormBase: React.FC<ConsultationFormBaseProps> = ({
  mode,
  initialData,
  onSubmit,
  isSubmitting = false,
  role,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<Consultation>({
    id: initialData?.id || 0,
    patientId: initialData?.patientId || initialData?.maladeId || 0,
    fullName: initialData?.fullName || "",
    telephone: initialData?.phone || "",
    dateOfBirth: initialData?.dateOfBirth || "",
    dateVisit: initialData?.dateVisit || new Date().toISOString().slice(0, 10),
    motif: initialData?.motif || "",
    doctorId: initialData?.doctorId || 0,
    doctorName: initialData?.doctorName || "",
    montant: initialData?.montant || 0,
    sommePaye: initialData?.sommePaye || 0,
    sommeImpaye: initialData?.sommeImpaye || 0,
    maladeId: initialData?.maladeId || 0,
  });

  // State pour les données créées (pour la génération PDF)
  const [createdPatientData, setCreatedPatientData] = useState<any>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [showPdfDownload, setShowPdfDownload] = useState(false);

  // State for constante data - always included
  const [constanteData, setConstanteData] = useState<Constante>({
    weight: 0,
    height: 0,
    bloodPressure: "",
    heartRate: 0,
    temperature: 0,
    reason: VisitReason.GENERAL_CONSULTATION,
    recordedAt: new Date().toISOString(),
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientMode, setPatientMode] = useState<"existing" | "new">(
    mode === "create" ? "existing" : "existing"
  );
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(
    initialData?.maladeId ?? (location as any)?.state?.selectedPatientId ?? null
  );

  const [patientData, setPatientData] = useState<PatientFormData>({
    firstName: "",
    lastName: "",
    username: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    phone: "",
    email: "",
    password: "",
    role: "PATIENT",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [patientErrors, setPatientErrors] = useState<Partial<PatientFormData>>(
    {}
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filtered patients based on search query (name, phone, email, etc.)
  const filteredPatients = patients.filter((patient) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
    const phone = patient.phone?.toLowerCase() || "";
    const email = patient.email?.toLowerCase() || "";
    const address = patient.address?.toLowerCase() || "";

    return (
      fullName.includes(searchLower) ||
      phone.includes(searchLower) ||
      email.includes(searchLower) ||
      address.includes(searchLower) ||
      (patient.dateOfBirth &&
        new Date(patient.dateOfBirth)
          .toLocaleDateString("fr-FR")
          .includes(searchLower))
    );
  });

  // Mapping des motifs pour affichage
  const reasonMap: { [key in VisitReason]: string } = {
    [VisitReason.GENERAL_CONSULTATION]: "Consultation générale",
    [VisitReason.PEDIATRIC_CONSULTATION]: "Consultation pédiatrique",
    [VisitReason.SPECIALIST_CONSULTATION]: "Consultation spécialisée",
    [VisitReason.RESPIRATORY_PHYSIOTHERAPY]: "Kinésithérapie respiratoire",
    [VisitReason.PRE_EMPLOYMENT_VISIT]: "Visite de pré-embauche",
    [VisitReason.GYNECOLOGY_CONSULTATION]: "Consultation gynécologique",
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.USERS.DOCTORS.SORTED);
        setDoctors(res.data);
      } catch (error) {
        console.error("Erreur lors du chargement des docteurs :", error);
        setFormError("Erreur lors du chargement des docteurs");
      }
    };
    fetchDoctors();

    const fetchPatients = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.PATIENTS.LIST);
        setPatients(res.data);
      } catch (error) {
        console.error("Erreur lors du chargement des patients :", error);
        setFormError("Erreur lors du chargement des patients");
      }
    };
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatientId) {
      const fetchPatientDetails = async () => {
        try {
          const res = await axiosInstance.get(
            API_ENDPOINTS.PATIENTS.GET_BY_ID(selectedPatientId)
          );
          const patient: Patient = res.data;
          setFormData((prev) => ({
            ...prev,
            fullName: `${patient.firstName} ${patient.lastName}`,
            telephone: patient.phone,
            dateOfBirth: patient.dateOfBirth || "",
            maladeId: patient.id,
          }));
        } catch (error) {
          console.error(
            "Erreur lors du chargement des détails du patient :",
            error
          );
          setFormError("Erreur lors du chargement des détails du patient");
        }
      };
      fetchPatientDetails();
    }
  }, [selectedPatientId]);

  // Calcul automatique de sommeImpaye
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      sommeImpaye: Math.max(0, prev.montant - prev.sommePaye),
    }));
  }, [formData.montant, formData.sommePaye]);

  // Fonction pour générer les données PDF
  const generatePdfData = (isNewPatient: boolean, responseData?: any) => {
    if (isNewPatient && responseData) {
      // Pour nouveau patient, utiliser les données retournées par l'API
      return {
        id: responseData.patient?.id || responseData.id || Date.now(),
        fullName: `${patientData.firstName} ${patientData.lastName}`,
        dateOfBirth: patientData.dateOfBirth,
        dateVisit: formData.dateVisit,
        motif: reasonMap[formData.motif as VisitReason] || formData.motif,
        doctorName: (() => {
          const doc = doctors.find((d) => d.id === formData.doctorId);
          if (doc) {
            return [doc.firstName, doc.lastName].filter(Boolean).join(" ");
          }
          return formData.doctorName || "";
        })(),
        montant: formData.montant,
        sommePaye: formData.sommePaye,
        sommeImpaye: formData.sommeImpaye,
        telephone: patientData.phone,
        adresse: patientData.address,
      };
    } else {
      // Pour patient existant
      const selectedPatient = patients.find((p) => p.id === selectedPatientId);
      return {
        id: selectedPatientId || Date.now(),
        fullName: formData.fullName,
        dateOfBirth: formData.dateOfBirth,
        dateVisit: formData.dateVisit,
        motif: reasonMap[formData.motif as VisitReason] || formData.motif,
        doctorName: formData.doctorName,
        montant: formData.montant,
        sommePaye: formData.sommePaye,
        sommeImpaye: formData.sommeImpaye,
        telephone: formData.telephone,
        adresse: selectedPatient?.address || "",
      };
    }
  };

  // Fonction pour générer et télécharger le PDF
  const generateAndDownloadPDF = async (pdfData: any) => {
    try {
      const pdfDoc = <PatientPDF patientInfo={pdfData} />;
      const blob = await pdf(pdfDoc).toBlob();
      setPdfBlob(blob);
      setCreatedPatientData(pdfData);
      setShowPdfDownload(true);

      // Téléchargement automatique
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fiche_patient_${pdfData.fullName.replace(/\s+/g, "_")}_${pdfData.id
        }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
    }
  };

  // cette fonction pour gérer la navigation au clavier
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredPatients.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredPatients.length
        ) {
          const selectedId = filteredPatients[highlightedIndex].id;
          setSelectedPatientId(selectedId);
          setSearchQuery(
            `${filteredPatients[highlightedIndex].firstName} ${filteredPatients[highlightedIndex].lastName}`
          );
          setIsDropdownOpen(false);
          setHighlightedIndex(-1);
        }
        break;
      case "Escape":
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatientId(patient.id);
    setSearchQuery(`${patient.firstName} ${patient.lastName}`);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.maladeId;
      return newErrors;
    });
    setFormError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ["doctorId", "montant", "sommePaye"].includes(name)
        ? Number(value)
        : value,
      ...(name === "doctorId"
        ? {
          doctorName: (() => {
            const doc = doctors.find((d) => d.id === Number(value));
            return doc ? [doc.firstName, doc.lastName].filter(Boolean).join(" ") : "";
          })(),
        }
        : {}),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete (newErrors as Record<string, any>)[name];
      return newErrors;
    });
    setFormError(null);
  };

  const handleConstanteChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setConstanteData((prev) => ({
      ...prev,
      [name]: ["weight", "height", "heartRate", "temperature"].includes(name)
        ? Number(value)
        : name === "recordedAt"
          ? new Date(value).toISOString()
          : value,
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
    setFormError(null);
  };

  const handleNewPatientChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setPatientData((prev) => ({ ...prev, [name]: value }));
    setPatientErrors((prev) => {
      const newErrors = { ...prev };
      delete (newErrors as Record<string, any>)[name];
      return newErrors;
    });
    setFormError(null);
  };

  const validateForm = (): boolean => {
    let valid = true;
    const newErrors: Record<string, string> = {};
    const newPatientErrors: Partial<PatientFormData> = {};

    if (patientMode === "new") {
      if (!patientData.firstName)
        newPatientErrors.firstName = "Le prénom est requis";
      if (!patientData.lastName)
        newPatientErrors.lastName = "Le nom est requis";
      // Email, password, and username are optional for new patients
      if (!patientData.dateOfBirth)
        newPatientErrors.dateOfBirth = "La date de naissance est requise";
      if (!patientData.gender) newPatientErrors.gender = "Le genre est requis";
      if (!patientData.address)
        newPatientErrors.address = "L'adresse est requise";
      if (!patientData.phone)
        newPatientErrors.phone = "Le téléphone est requis";
      // Email and password are optional for new patients
      if (Object.keys(newPatientErrors).length > 0) valid = false;
      setPatientErrors(newPatientErrors);
    } else {
      if (!selectedPatientId) {
        newErrors.maladeId = "Le patient est requis";
        valid = false;
      }
    }

    if (!formData.dateVisit)
      newErrors.dateVisit = "La date de visite est requise";
    if (!formData.motif) newErrors.motif = "Le motif est requis";
    if (!formData.doctorId) newErrors.doctorId = "Le docteur est requis";
    if (formData.montant === undefined || formData.montant < 0)
      newErrors.montant = "Le montant est requis et doit être positif";
    if (formData.sommePaye === undefined || formData.sommePaye < 0)
      newErrors.sommePaye = "La somme payée est requise et doit être positive";
    if (formData.sommeImpaye < 0)
      newErrors.sommeImpaye = "La somme impayée ne peut pas être négative";

    // Constantes optionnelles: aucune validation obligatoire ici

    if (Object.keys(newErrors).length > 0) valid = false;
    setErrors(newErrors);

    return valid;
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
      navigate(`/${role}/dashboard/patient`);
    }, 5000); // Augmenté à 5 secondes pour laisser le temps de télécharger
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setFormError("No token found");
      return;
    }

    setIsLoading(true);
    setFormError(null);

    try {
      if (patientMode === "new") {
        const payload: PatientWithConstante = {
          patient: {
            firstName: patientData.firstName,
            lastName: patientData.lastName,
            dateOfBirth: patientData.dateOfBirth,
            gender: patientData.gender,
            address: patientData.address || null,
            phone: patientData.phone,
            email: patientData.email || null, // Send null instead of empty string
            doctorId: Number(formData.doctorId),
            montant: formData.montant,
            sommePaye: formData.sommePaye,
            dateVisit: formData.dateVisit,
            motif: formData.motif as VisitReason,
          },
          constante: {
            id: 0,
            firstName: patientData.firstName,
            lastName: patientData.lastName,
            patientId: 0,
            weight: constanteData.weight,
            height: constanteData.height,
            bloodPressure: constanteData.bloodPressure,
            heartRate: constanteData.heartRate,
            temperature: constanteData.temperature,
            reason: formData.motif as VisitReason,
            recordedAt: constanteData.recordedAt,
            doctorId: Number(formData.doctorId),
          },
        };

        const response = await axiosInstance.post(
          API_ENDPOINTS.PATIENTS.WITH_CONSTANTE,
          payload
        );

        // Générer et télécharger le PDF pour nouveau patient
        const pdfData = generatePdfData(true, response.data);
        await generateAndDownloadPDF(pdfData);

        if (onSubmit) await onSubmit(payload as PatientWithConstante);
        showSuccessMessage(
          "Consultation créée avec succès pour le nouveau patient ! Le PDF a été téléchargé automatiquement."
        );
      } else {
        // For existing patient, create constante with patient and doctor IDs
        const selectedPatient = patients.find(
          (p) => p.id === selectedPatientId
        );
        const constantePayload: Partial<MedicalConstant> = {
          id: 0,
          firstName: selectedPatient?.firstName || "",
          lastName: selectedPatient?.lastName || "",
          patientId: Number(selectedPatientId),
          weight: constanteData.weight,
          height: constanteData.height,
          bloodPressure: constanteData.bloodPressure,
          heartRate: constanteData.heartRate,
          temperature: constanteData.temperature,
          reason: formData.motif as VisitReason,
          recordedAt: constanteData.recordedAt,
          doctorId: Number(formData.doctorId),
        };

        const response = await axiosInstance.post(
          API_ENDPOINTS.CONSTANTES.CREATE,
          constantePayload as any
        );

        // Générer et télécharger le PDF pour patient existant
        const pdfData = generatePdfData(false, response.data);
        await generateAndDownloadPDF(pdfData);

        if (onSubmit) await onSubmit(formData);
        showSuccessMessage(
          "Constantes médicales enregistrées avec succès pour le patient existant ! Le PDF a été téléchargé automatiquement."
        );
      }
    } catch (err: any) {
      console.error("Error submitting:", err);

      // More detailed error handling
      if (err.response?.data?.message) {
        setFormError(`Erreur: ${err.response.data.message}`);
      } else if (err.response?.status === 400) {
        setFormError("Données invalides. Veuillez vérifier tous les champs.");
      } else if (err.response?.status === 401) {
        setFormError("Non autorisé. Veuillez vous reconnecter.");
      } else if (err.response?.status === 500) {
        setFormError("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setFormError("Erreur lors de la soumission. Veuillez réessayer.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 w-full max-w-5xl animate-slideInUp">
      {/* Success Message avec PDF Download */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div className="text-green-800 font-medium">{successMessage}</div>
          </div>
          {showPdfDownload && createdPatientData && (
            <div className="mt-3 p-3 bg-white border border-green-200 rounded-md">
              <div className="flex items-center justify-between">
                <span className="text-sm text-green-700">
                  Fiche patient générée automatiquement
                </span>
                {pdfBlob && (
                  <a
                    href={URL.createObjectURL(pdfBlob)}
                    download={`fiche_patient_${createdPatientData.fullName.replace(
                      /\s+/g,
                      "_"
                    )}_${createdPatientData.id}.pdf`}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger à nouveau
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {formError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {formError}
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {mode === "create"
                ? "Créer une consultation"
                : "Mettre à jour la consultation"}
            </h1>
            <p className="text-gray-600 text-sm">
              {mode === "create"
                ? "Ajoutez une nouvelle consultation. Le PDF sera généré automatiquement."
                : "Modifiez les informations de la consultation."}
            </p>
          </div>
        </div>
        <Link
          to={`/${role}/dashboard/patient`}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors shadow-md"
          aria-label="Retour à la gestion des consultations"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Retour</span>
        </Link>
      </div>
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-6"
      >
        {mode === "create" && (
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de patient
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={patientMode === "existing"}
                  onChange={() => setPatientMode("existing")}
                  className="mr-2"
                />
                Patient existant
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={patientMode === "new"}
                  onChange={() => setPatientMode("new")}
                  className="mr-2"
                />
                Nouveau patient
              </label>
            </div>
          </div>
        )}
        {(patientMode === "existing" || mode === "update") && (
          <div className="col-span-2" ref={dropdownRef}>
            <label
              htmlFor="patientId"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Patient*
            </label>

            {/* Champ de recherche avec dropdown interactif */}
            <div className="grid grid-cols-1 gap-3">
              {/* Sélecteur de patient */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sélectionner un patient existant
                </label>
                <select
                  value={selectedPatientId || ""}
                  onChange={(e) => {
                    const patientId = e.target.value
                      ? parseInt(e.target.value)
                      : null;
                    const patient = patients.find((p) => p.id === patientId);
                    if (patient) {
                      handlePatientSelect(patient);
                    } else {
                      setSelectedPatientId(null);
                      setFormData((prev) => ({ ...prev, maladeId: 0 }));
                    }
                  }}
                  disabled={mode === "update"}
                  className={`w-full px-4 py-2.5 border ${errors.maladeId ? "border-red-500" : "border-gray-200"
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors ${mode === "update" ? "cursor-not-allowed opacity-60" : ""
                    }`}
                >
                  <option value="">-- Sélectionner un patient --</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.firstName} {patient.lastName}{" "}
                      {patient.phone ? `(${patient.phone})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ou rechercher un patient */}
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Ou rechercher un patient
                  </label>
                  <span className="text-xs text-gray-500">
                    {searchQuery && `${filteredPatients.length} résultat(s)`}
                  </span>
                </div>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Tapez pour rechercher un patient..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsDropdownOpen(true);
                    setHighlightedIndex(-1);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  onKeyDown={handleKeyDown}
                  disabled={mode === "update"}
                  className={`w-full pl-11 pr-12 py-2.5 border ${errors.maladeId ? "border-red-500" : "border-gray-200"
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all ${mode === "update" ? "cursor-not-allowed opacity-60" : ""
                    }`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setIsDropdownOpen(false);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                    aria-label="Effacer la recherche"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}

                {/* Dropdown des résultats */}
                {isDropdownOpen && mode !== "update" && (
                  <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                    {/* Afficher tous les patients si pas de recherche, sinon les filtrés */}
                    {(searchQuery ? filteredPatients : patients).length > 0 ? (
                      <div className="py-2">
                        {/* En-tête avec compteur */}
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 sticky top-0">
                          <p className="text-xs text-gray-600 font-medium">
                            {searchQuery
                              ? `${filteredPatients.length} résultat${filteredPatients.length > 1 ? "s" : ""
                              } trouvé${filteredPatients.length > 1 ? "s" : ""
                              }`
                              : `${patients.length} patient${patients.length > 1 ? "s" : ""
                              } au total`}
                          </p>
                        </div>

                        {(searchQuery ? filteredPatients : patients).map(
                          (patient, index) => (
                            <button
                              key={patient.id}
                              type="button"
                              onClick={() => handlePatientSelect(patient)}
                              className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-start gap-3 ${index === highlightedIndex ? "bg-blue-50" : ""
                                } ${selectedPatientId === patient.id
                                  ? "bg-blue-100"
                                  : ""
                                }`}
                            >
                              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <UserPlus className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900">
                                  {patient.firstName} {patient.lastName}
                                </div>
                                <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
                                  <span className="inline-flex items-center">
                                    <svg
                                      className="w-3.5 h-3.5 mr-1 text-gray-400"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M20 22.621l-3.521-3.52a10.9 10.9 0 10-1.4 1.4l3.52 3.521 1.4-1.401zm-10.82-10.82a8.5 8.5 0 1112.2 0l-1.43 1.43a6.5 6.5 0 10-9.34 0l-1.43-1.43zM18 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {patient.phone}
                                  </span>
                                  {patient.email && (
                                    <span className="inline-flex items-center">
                                      <svg
                                        className="w-3.5 h-3.5 mr-1 text-gray-400"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M12 12.713l-11.985-9.713h23.97l-11.985 9.713zm0 2.574l-12-9.725v15.438h24v-15.438l-12 9.725z" />
                                      </svg>
                                      <span
                                        className="truncate max-w-[150px]"
                                        title={patient.email}
                                      >
                                        {patient.email}
                                      </span>
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                                  {patient.dateOfBirth && (
                                    <span className="flex items-center">
                                      <svg
                                        className="w-3.5 h-3.5 mr-1"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M19.5 3h-3V1.5H15V3H9V1.5H7.5V3h-3C3.675 3 3 3.675 3 4.5v15c0 .825.675 1.5 1.5 1.5h15c.825 0 1.5-.675 1.5-1.5v-15c0-.825-.675-1.5-1.5-1.5zm0 16.5h-15V9h15v10.5zm-15-12v-3h3V6h3V4.5h3V6h3V4.5h3v3h-15z" />
                                      </svg>
                                      {new Date(
                                        patient.dateOfBirth
                                      ).toLocaleDateString("fr-FR")}
                                    </span>
                                  )}
                                  {patient.address && (
                                    <span className="flex items-center">
                                      <svg
                                        className="w-3.5 h-3.5 mr-1"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                      </svg>
                                      <span
                                        className="truncate max-w-[200px]"
                                        title={patient.address}
                                      >
                                        {patient.address}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Search className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          Aucun patient trouvé
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          Essayez avec un autre nom, téléphone, email ou adresse
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {errors.maladeId && (
              <p className="mt-1 text-sm text-red-500">{errors.maladeId}</p>
            )}

            {/* Affichage du patient sélectionné */}
            {selectedPatientId && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Patient sélectionné
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      {formData.fullName}
                    </p>
                    {formData.telephone && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formData.telephone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {patientMode === "new" && (
          <>
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Prénom*
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={patientData.firstName}
                onChange={handleNewPatientChange}
                placeholder="Prénom du patient"
                className={`w-full px-4 py-2.5 border ${patientErrors.firstName ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.firstName && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.firstName}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nom*
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={patientData.lastName}
                onChange={handleNewPatientChange}
                placeholder="Nom du patient"
                className={`w-full px-4 py-2.5 border ${patientErrors.lastName ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.lastName && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.lastName}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nom d'utilisateur (optionnel)
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={patientData.username}
                onChange={handleNewPatientChange}
                placeholder="Nom d'utilisateur"
                className={`w-full px-4 py-2.5 border ${patientErrors.username ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.username && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.username}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="patientDateOfBirth"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date de naissance*
              </label>
              <input
                type="date"
                id="patientDateOfBirth"
                name="dateOfBirth"
                value={patientData.dateOfBirth}
                onChange={handleNewPatientChange}
                className={`w-full px-4 py-2.5 border ${patientErrors.dateOfBirth
                  ? "border-red-500"
                  : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-all`}
              />
              {patientErrors.dateOfBirth && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.dateOfBirth}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Genre*
              </label>
              <select
                id="gender"
                name="gender"
                value={patientData.gender}
                onChange={handleNewPatientChange}
                className={`w-full px-4 py-2.5 border ${patientErrors.gender ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors`}
              >
                <option value="">-- Sélectionner --</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
              {patientErrors.gender && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.gender}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Adresse*
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={patientData.address}
                onChange={handleNewPatientChange}
                placeholder="Adresse"
                className={`w-full px-4 py-2.5 border ${patientErrors.address ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.address && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.address}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Téléphone*
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={patientData.phone}
                onChange={handleNewPatientChange}
                placeholder="+123 456 7890"
                className={`w-full px-4 py-2.5 border ${patientErrors.phone ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.phone && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.phone}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email (optionnel)
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={patientData.email}
                onChange={handleNewPatientChange}
                placeholder="email@exemple.com (optionnel)"
                className={`w-full px-4 py-2.5 border ${patientErrors.email ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.email && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.email}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mot de passe (optionnel)
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={patientData.password}
                onChange={handleNewPatientChange}
                placeholder="Mot de passe (optionnel)"
                className={`w-full px-4 py-2.5 border ${patientErrors.password ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
              />
              {patientErrors.password && (
                <p className="mt-1 text-sm text-red-500">
                  {patientErrors.password}
                </p>
              )}
            </div>
          </>
        )}

        <div>
          <label
            htmlFor="dateVisit"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Date de visite*
          </label>
          <input
            type="date"
            id="dateVisit"
            name="dateVisit"
            value={formData.dateVisit}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border ${errors.dateVisit ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-all`}
          />
          {errors.dateVisit && (
            <p className="mt-1 text-sm text-red-500">{errors.dateVisit}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="motif"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Motif de consultation*
          </label>
          <select
            id="motif"
            name="motif"
            value={formData.motif}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border ${errors.motif ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors`}
          >
            <option value="">-- Sélectionner un motif --</option>
            {Object.values(VisitReason).map((reason) => (
              <option key={reason} value={reason}>
                {reasonMap[reason]}
              </option>
            ))}
          </select>
          {errors.motif && (
            <p className="mt-1 text-sm text-red-500">{errors.motif}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="doctorId"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Docteur*
          </label>
          <select
            id="doctorId"
            name="doctorId"
            value={formData.doctorId}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border ${errors.doctorId ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors`}
            aria-required="true"
          >
            <option value="">-- Sélectionner un docteur --</option>
            {doctors.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.firstName} {doctor.lastName}
              </option>
            ))}
          </select>
          {errors.doctorId && (
            <p className="mt-1 text-sm text-red-500">{errors.doctorId}</p>
          )}
        </div>

        <div className="col-span-2">
          <h3 className="text-lg font-medium text-gray-700 mb-4">
            Constantes médicales
          </h3>
        </div>

        <div>
          <label
            htmlFor="weight"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Poids (kg)
          </label>
          <input
            type="number"
            id="weight"
            name="weight"
            value={constanteData.weight}
            onChange={handleConstanteChange}
            placeholder="0"
            min="0"
            step="0.1"
            className={`w-full px-4 py-2.5 border ${errors.weight ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.weight && (
            <p className="mt-1 text-sm text-red-500">{errors.weight}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="height"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Taille (cm)
          </label>
          <input
            type="number"
            id="height"
            name="height"
            value={constanteData.height}
            onChange={handleConstanteChange}
            placeholder="0"
            min="0"
            className={`w-full px-4 py-2.5 border ${errors.height ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.height && (
            <p className="mt-1 text-sm text-red-500">{errors.height}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="bloodPressure"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Pression artérielle
          </label>
          <input
            type="text"
            id="bloodPressure"
            name="bloodPressure"
            value={constanteData.bloodPressure}
            onChange={handleConstanteChange}
            placeholder="120/80"
            className={`w-full px-4 py-2.5 border ${errors.bloodPressure ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.bloodPressure && (
            <p className="mt-1 text-sm text-red-500">{errors.bloodPressure}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="heartRate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Rythme cardiaque (bpm)
          </label>
          <input
            type="number"
            id="heartRate"
            name="heartRate"
            value={constanteData.heartRate}
            onChange={handleConstanteChange}
            placeholder="0"
            min="0"
            className={`w-full px-4 py-2.5 border ${errors.heartRate ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.heartRate && (
            <p className="mt-1 text-sm text-red-500">{errors.heartRate}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="temperature"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Température (°C)
          </label>
          <input
            type="number"
            id="temperature"
            name="temperature"
            value={constanteData.temperature}
            onChange={handleConstanteChange}
            placeholder="0"
            min="0"
            step="0.1"
            className={`w-full px-4 py-2.5 border ${errors.temperature ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.temperature && (
            <p className="mt-1 text-sm text-red-500">{errors.temperature}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="montant"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Montant total (MRU)*
          </label>
          <input
            type="number"
            id="montant"
            name="montant"
            value={formData.montant}
            onChange={handleChange}
            placeholder="0"
            min="0"
            className={`w-full px-4 py-2.5 border ${errors.montant ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.montant && (
            <p className="mt-1 text-sm text-red-500">{errors.montant}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="sommePaye"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Somme payée (MRU)*
          </label>
          <input
            type="number"
            id="sommePaye"
            name="sommePaye"
            value={formData.sommePaye}
            onChange={handleChange}
            placeholder="0"
            min="0"
            className={`w-full px-4 py-2.5 border ${errors.sommePaye ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.sommePaye && (
            <p className="mt-1 text-sm text-red-500">{errors.sommePaye}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="sommeImpaye"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Somme impayée (MRU)
          </label>
          <input
            type="number"
            id="sommeImpaye"
            name="sommeImpaye"
            value={formData.sommeImpaye}
            disabled
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-800 transition-all cursor-not-allowed"
          />
          {errors.sommeImpaye && (
            <p className="mt-1 text-sm text-red-500">{errors.sommeImpaye}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="recordedAt"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Date d'enregistrement
          </label>
          <input
            type="datetime-local"
            id="recordedAt"
            name="recordedAt"
            value={constanteData.recordedAt.slice(0, 16)}
            onChange={handleConstanteChange}
            className={`w-full px-4 py-2.5 border ${errors.recordedAt ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-all`}
          />
          {errors.recordedAt && (
            <p className="mt-1 text-sm text-red-500">{errors.recordedAt}</p>
          )}
        </div>

        <div className="md:col-span-2 flex justify-end gap-3 mt-6">
          <Link
            to={`/${role}/dashboard/patient`}
            className="px-6 py-3 border border-gray-200 bg-gray-50 rounded-lg text-gray-700 hover:bg-gray-100 text-sm font-medium transition-colors"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={isLoading || isSubmitting}
            className={`px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium ${isLoading || isSubmitting
              ? "opacity-50 cursor-not-allowed"
              : "hover:from-blue-600 hover:to-blue-700"
              } flex items-center justify-center transition-all shadow-md hover:shadow-lg`}
            aria-label={
              mode === "create"
                ? "Créer la consultation et générer le PDF"
                : "Enregistrer les modifications"
            }
          >
            {isLoading || isSubmitting ? (
              <>
                <Activity className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                {mode === "create"
                  ? "Créer la consultation et générer le PDF"
                  : "Enregistrer les modifications"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsultationFormBase;
